import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../shared/DashboardLayout';
import { grievanceAPI } from '../../services/api';
import '../../styles/Grievance.css';

// ── Role → dashboard URL map ──────────────────────────────────────────────────
const DASHBOARD_URL = {
  GRIEVANCE_OFFICER:     '/grievance-officer-dashboard',
  ADMIN:                 '/admin-dashboard',
  STUDENT:               '/student-dashboard',
  FACULTY:               '/faculty-dashboard',
  HOD:                   '/hod-dashboard',
  PRINCIPAL:             '/admin-dashboard',
  DEAN:                  '/admin-dashboard',
  FINANCE:               '/finance-dashboard',
  HOSTEL_ADMIN:          '/hostel-dashboard',
  LIBRARY_STAFF:         '/library-dashboard',
  TRANSPORT_MANAGER:     '/transport-dashboard',
  recepsonist:           '/reception',
  LAB_TECH_MICROBIOLOGY: '/lab-dashboard',
  LAB_TECH_PATHOLOGY:    '/lab-dashboard',
  LAB_TECH_BIOCHEMISTRY: '/lab-dashboard',
  BILLING_MEMBER:        '/lab-billing',
  BILLING_MANAGER:       '/billing-manager',
  SAMPLE_COLLECTOR:      '/sample-collection',
  REPORT_GENERATION:     '/report-generation',
};

const getDashboardUrl = (user) => {
  if (!user) return '/login';
  if (user.role === 'FACULTY' && user?.profile?.faculty_subrole === 'HEAD_OF_DEPARTMENT') {
    return '/hod-dashboard';
  }
  return DASHBOARD_URL[user.role] || '/login';
};

// ── Constants ─────────────────────────────────────────────────────────────────
const ESCALATION_ROLES = ['ADMIN', 'PRINCIPAL', 'DEAN'];

const STATUS_COLORS = {
  ASSIGNED:     'badge-assigned',
  UNDER_REVIEW: 'badge-under_review',
  RESOLVED:     'badge-resolved',
  REJECTED:     'badge-rejected',
  ESCALATED:    'badge-escalated',
  REOPENED:     'badge-reopened',
};

const PRIORITY_COLORS = {
  LOW:    'badge-low',
  MEDIUM: 'badge-medium',
  HIGH:   'badge-high',
};

const DEPT_ROLE_LABELS = {
  BILLING_MANAGER:       'Billing Manager',
  REPORT_GENERATION:     'Report Generation',
  SAMPLE_COLLECTOR:      'Sample Collector',
  LAB_TECH_MICROBIOLOGY: 'Lab – Microbiology',
  LAB_TECH_PATHOLOGY:    'Lab – Pathology',
  LAB_TECH_BIOCHEMISTRY: 'Lab – Biochemistry',
  recepsonist:           'Receptionist',
  HOSTEL_ADMIN:          'Hostel Admin',
  LIBRARY_STAFF:         'Library Staff',
  TRANSPORT_MANAGER:     'Transport Manager',
  FACULTY:               'Faculty',
  ADMIN:                 'Admin',
  PRINCIPAL:             'Principal',
  DEAN:                  'Dean',
  FINANCE:               'Finance',
};

// ── Component ─────────────────────────────────────────────────────────────────
const DepartmentActionPage = () => {
  const navigate     = useNavigate();
  const { user }     = useAuth();

  // Derived role flags — inside the component so `user` is available
  const isEscalationRole = ESCALATION_ROLES.includes(user?.role);

  const [grievances, setGrievances] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState('');

  useEffect(() => { loadGrievances(); }, [filter]);

  const loadGrievances = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status = filter;
      const data = await grievanceAPI.getDepartmentGrievances(params);
      setGrievances(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      console.error(e);
      setGrievances([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });

  const timeAgo = (d) => {
    const s = Math.floor((Date.now() - new Date(d)) / 1000);
    if (s < 60)    return 'Just now';
    if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  // ── Stats — escalation roles count ESCALATED as "needs action" ───────────────
  const pendingCount  = isEscalationRole
    ? grievances.filter(g => g.status === 'ESCALATED').length
    : grievances.filter(g => g.status === 'ASSIGNED').length;
  const inReviewCount = grievances.filter(g => g.status === 'UNDER_REVIEW').length;
  const resolvedCount = grievances.filter(g => g.status === 'RESOLVED').length;

  // ── Filter chips differ per role type ────────────────────────────────────────
  const filterChips = isEscalationRole
    ? [
        { value: '',             label: 'All' },
        { value: 'ESCALATED',    label: 'Needs Resolution' },
        { value: 'UNDER_REVIEW', label: 'Under Review' },
        { value: 'RESOLVED',     label: 'Resolved' },
        { value: 'REJECTED',     label: 'Rejected' },
      ]
    : [
        { value: '',             label: 'All' },
        { value: 'ASSIGNED',     label: 'Needs Action' },
        { value: 'UNDER_REVIEW', label: 'Under Review' },
        { value: 'RESOLVED',     label: 'Resolved' },
        { value: 'ESCALATED',    label: 'Escalated' },
        { value: 'REOPENED',     label: 'Reopened' },
      ];

  // ── Page title ───────────────────────────────────────────────────────────────
  const pageTitle = isEscalationRole
    ? 'Escalated Grievances'
    : 'My Department Grievances';

  return (
    <DashboardLayout title={pageTitle}>
      <div className="grievance-container">

        {/* ── Back to dashboard ───────────────────────────────────────────── */}
        <button
          onClick={() => navigate(getDashboardUrl(user))}
          style={{
            background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer',
            marginBottom: '1rem', padding: 0, fontSize: '0.875rem',
            display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24"
            stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Dashboard
        </button>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div style={{ marginBottom: '1.25rem' }}>
          {isEscalationRole ? (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem',
              }}>
                <span style={{ fontSize: '1.1rem' }}>⬆️</span>
                <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700, color: '#111827' }}>
                  Escalated Grievances —{' '}
                  <span style={{ color: '#f97316' }}>
                    {DEPT_ROLE_LABELS[user?.role] || user?.role} Queue
                  </span>
                </h2>
              </div>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                Grievances escalated to {DEPT_ROLE_LABELS[user?.role] || user?.role} for resolution
              </p>
            </>
          ) : (
            <>
              <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.125rem', fontWeight: 700, color: '#111827' }}>
                Department:{' '}
                <span style={{ color: '#6366f1' }}>
                  {DEPT_ROLE_LABELS[user?.role] || user?.role}
                </span>
              </h2>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                Grievances assigned to your department for action
              </p>
            </>
          )}
        </div>

        {/* ── Mini stats ──────────────────────────────────────────────────── */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '0.75rem', marginBottom: '1.25rem',
        }}>
          {[
            {
              label: isEscalationRole ? 'Needs Resolution' : 'Needs Action',
              value: pendingCount,
              color: isEscalationRole ? '#f97316' : '#3b82f6',
            },
            { label: 'Under Review', value: inReviewCount, color: '#8b5cf6' },
            { label: 'Resolved',     value: resolvedCount, color: '#10b981' },
          ].map(s => (
            <div key={s.label} className="grievance-stat-card" style={{ padding: '0.875rem' }}>
              <div className="grievance-stat-number"
                style={{ color: s.color, fontSize: '1.75rem' }}>{s.value}</div>
              <div className="grievance-stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── Status filters ──────────────────────────────────────────────── */}
        <div className="grievance-filters" style={{ marginBottom: '1.25rem' }}>
          {filterChips.map(f => (
            <button
              key={f.value}
              className={`filter-chip ${filter === f.value ? 'active' : ''}`}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* ── List ────────────────────────────────────────────────────────── */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="spinner-large" />
            <p style={{ color: '#6b7280', marginTop: '0.75rem' }}>Loading…</p>
          </div>
        ) : grievances.length === 0 ? (
          <div className="grievance-empty">
            <svg width="64" height="64" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p>{isEscalationRole ? 'No escalated grievances' : 'No grievances assigned'}</p>
            <span>
              {isEscalationRole
                ? `No grievances have been escalated to ${DEPT_ROLE_LABELS[user?.role] || user?.role}.`
                : 'Your department has no pending grievances.'}
            </span>
          </div>
        ) : (
          grievances.map(g => (
            <div
              key={g.id}
              className="grievance-card"
              onClick={() => navigate(`/grievances/${g.id}`)}
              style={
                isEscalationRole && g.status === 'ESCALATED'
                  ? { borderLeft: '3px solid #f97316' }
                  : {}
              }
            >
              <div className="grievance-card-header">
                <div style={{ flex: 1 }}>

                  {/* Badges row */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    marginBottom: '0.375rem', flexWrap: 'wrap',
                  }}>
                    <span className="grievance-id">{g.grievance_id}</span>
                    <span className={`grievance-badge ${STATUS_COLORS[g.status] || 'badge-closed'}`}>
                      {g.status.replace('_', ' ')}
                    </span>
                    <span className={`grievance-badge ${PRIORITY_COLORS[g.priority] || 'badge-medium'}`}>
                      {g.priority}
                    </span>
                    <span style={{
                      fontSize: '0.75rem', color: '#9ca3af',
                      background: '#f3f4f6', padding: '2px 8px', borderRadius: 4,
                    }}>
                      {g.category_display}
                    </span>
                  </div>

                  <p className="grievance-subject">{g.subject}</p>

                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280' }}>
                    Submitted by:{' '}
                    <strong>{g.submitted_by?.full_name || g.submitted_by?.username}</strong>
                    {!isEscalationRole && g.assigned_to && (
                      <> · Assigned to:{' '}
                        <strong>{g.assigned_to.full_name || g.assigned_to.username}</strong>
                      </>
                    )}
                    {isEscalationRole && g.escalated_to && (
                      <> · Escalated to:{' '}
                        <strong style={{ color: '#f97316' }}>
                          {DEPT_ROLE_LABELS[g.escalated_to] || g.escalated_to}
                        </strong>
                      </>
                    )}
                  </p>
                </div>

                {/* Right side — time + priority alert + action button */}
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                    {timeAgo(g.updated_at)}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#d1d5db' }}>
                    {formatDate(g.created_at)}
                  </div>
                  {g.priority === 'HIGH' && !['RESOLVED', 'CLOSED'].includes(g.status) && (
                    <div style={{ fontSize: '0.7rem', color: '#ef4444', fontWeight: 700, marginTop: 4 }}>
                      ⚠ HIGH PRIORITY
                    </div>
                  )}
                  <button
                    className="btn-grievance-outline"
                    style={{ marginTop: '0.5rem', fontSize: '0.75rem', padding: '0.25rem 0.75rem' }}
                    onClick={e => { e.stopPropagation(); navigate(`/grievances/${g.id}`); }}
                  >
                    {isEscalationRole && g.status === 'ESCALATED'
                      ? 'Resolve'
                      : g.status === 'ASSIGNED'
                        ? 'Take Action'
                        : 'View'}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}

      </div>
    </DashboardLayout>
  );
};

export default DepartmentActionPage;